import React, { useEffect, useRef, useState, useContext } from 'react';
import { StoreHeaderDataContext } from '../../context-api/StoreHeaderDataProvider';
import { localStorageKeys } from '../../global/constants';
// TODO: update to your API controller path / function name

// ─────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

const ISSUE_TYPES = [
  { value: 'ordering', label: 'Ordering & checkout' },
  { value: 'payment', label: 'Payment' },
  { value: 'delivery', label: 'Delivery & tracking' },
  { value: 'crash', label: 'App crashed or froze' },
  { value: 'display', label: 'Something looks off' },
  { value: 'other', label: 'Other' },
];

const LOCATIONS = [
  { value: '', label: 'Choose a page' },
  { value: 'home', label: 'Home / browsing restaurants' },
  { value: 'menu', label: 'Restaurant menu' },
  { value: 'cart', label: 'Cart & checkout' },
  { value: 'tracking', label: 'Order tracking' },
  { value: 'account', label: 'Account & sign-in' },
  { value: 'other', label: 'Somewhere else' },
];

const FREQUENCIES = [
  { value: 'once', label: 'Just once' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'always', label: 'Every time' },
];

const initialForm = {
  type: '',
  where: '',
  frequency: '',
  description: '',
  email: '',
  contact: true,
};

// ─────────────────────────────────────────────
// Styles (light theme)
// ─────────────────────────────────────────────
const css = `
.rb { --navy:#121A2C; --orange:#FF7B29; --orange-text:#B4470C; --orange-tint:#FFF6EF; --orange-line:#F0A877;
  --bg:#F4F5F8; --card:#FFFFFF; --line:#E6E8EE; --input:#D5D9E2; --text:#121A2C; --text2:#3A4256; --muted:#5B6275; --error:#B42318;
  min-height: 100vh; background: var(--bg); color: var(--text); font-family: Inter, system-ui, -apple-system, sans-serif; }
.rb *, .rb *::before, .rb *::after { box-sizing: border-box; }
.rb :focus-visible { outline: 2px solid var(--orange-text); outline-offset: 2px; }
.rb-sr { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0; }

/* Header bar stays full width; its content lines up with the form column below
   (.rb-wrap = centered 760px with 20px side padding → content starts at (100% - 760px)/2 + 20px) */
.rb-top { height:64px; background:var(--card); border-bottom:1px solid var(--line); display:flex; align-items:center; justify-content:flex-start; gap:16px; padding:0 max(20px, calc((100% - 760px) / 2 + 20px)); }
.rb-top__brand { font-size:20px; font-weight:700; }
.rb-top__title { display:none; font-size:17px; font-weight:700; }
.rb-back { display:inline-flex; align-items:center; gap:6px; min-height:44px; margin-left:-4px; padding:0 4px; border:0; background:none; color:var(--orange-text); font:600 14px Inter, system-ui, sans-serif; cursor:pointer; }
.rb-back:hover { color:#8A3508; }

.rb-wrap { max-width:760px; margin:0 auto; padding:48px 20px 64px; }
.rb-intro h1 { margin:0 0 8px; font-size:32px; font-weight:700; letter-spacing:-0.01em; }
.rb-intro p { margin:0 0 28px; font-size:16px; line-height:1.5; color:var(--text2); }
.rb-card { background:var(--card); border:1px solid var(--line); border-radius:16px; padding:32px; }
.rb-form { display:flex; flex-direction:column; gap:28px; }

.rb fieldset { border:0; margin:0; padding:0; min-width:0; }
.rb-label, .rb legend { display:block; padding:0; margin-bottom:10px; font-size:15px; font-weight:600; }
.rb-opt { font-weight:400; color:var(--muted); }
.rb-hint { display:block; margin:-4px 0 8px; font-size:14px; color:var(--muted); }
.rb-field { display:flex; flex-direction:column; }
.rb-row2 { display:grid; grid-template-columns:repeat(auto-fit, minmax(260px, 1fr)); gap:24px; }

.rb-chips { display:flex; flex-wrap:wrap; gap:8px; }
.rb-chip input, .rb-seg input { position:absolute; opacity:0; width:1px; height:1px; }
.rb-chip span { min-height:44px; display:inline-flex; align-items:center; gap:6px; padding:0 16px; border-radius:999px; border:1px solid var(--input); background:var(--card); color:#2A3145; font-size:14px; font-weight:500; cursor:pointer; user-select:none; }
.rb-chip svg { display:none; }
.rb-chip input:checked + span { background:var(--navy); border-color:var(--navy); color:#fff; }
.rb-chip input:checked + span svg { display:block; }
.rb-chip input:focus-visible + span, .rb-seg input:focus-visible + span { outline:2px solid var(--orange-text); outline-offset:2px; }

.rb-seg { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:4px; padding:4px; background:var(--line); border-radius:12px; }
.rb-seg label { position:relative; }
.rb-seg span { height:40px; display:flex; align-items:center; justify-content:center; border-radius:9px; font-size:13px; font-weight:500; color:var(--text2); cursor:pointer; }
.rb-seg input:checked + span { background:var(--card); color:var(--text); font-weight:600; box-shadow:0 1px 2px rgba(18,26,44,.12); }

.rb select, .rb input[type="email"], .rb textarea { width:100%; border:1px solid var(--input); border-radius:10px; background:var(--card); color:var(--text); font:16px Inter, system-ui, sans-serif; }
.rb select, .rb input[type="email"] { height:48px; padding:0 14px; }
.rb input[type="email"] { max-width:420px; }
.rb textarea { min-height:140px; padding:12px 14px; line-height:1.5; resize:vertical; }
.rb [aria-invalid="true"] { border-color:var(--error); }
.rb-error { margin-top:8px; font-size:13px; font-weight:500; color:var(--error); }

.rb-check { display:flex; align-items:center; gap:10px; min-height:44px; margin-top:4px; font-size:14px; color:#2A3145; cursor:pointer; }
.rb-check input { width:18px; height:18px; margin:0; accent-color:var(--navy); }

.rb-shots-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:12px; }
.rb-shots-head .rb-label { margin:0; }
.rb-count { font-size:14px; color:var(--muted); }
.rb-drop { position:relative; display:flex; flex-direction:column; align-items:center; gap:8px; text-align:center; padding:28px 20px; border:1.5px dashed var(--orange-line); border-radius:12px; background:var(--orange-tint); cursor:pointer; }
.rb-drop.is-over { background:#FFE9D9; border-color:var(--orange); }
.rb-drop:focus-within { outline:2px solid var(--orange-text); outline-offset:2px; }
.rb-drop__icon { width:44px; height:44px; border-radius:999px; background:var(--card); color:var(--orange-text); display:flex; align-items:center; justify-content:center; }
.rb-drop__main { font-size:15px; color:#2A3145; }
.rb-drop__main u { color:var(--orange-text); font-weight:600; text-underline-offset:2px; }
.rb-drop__sub { font-size:13px; color:var(--muted); }

.rb-thumbs { display:grid; grid-template-columns:repeat(auto-fill, minmax(120px, 1fr)); gap:12px; margin:12px 0 0; padding:0; list-style:none; }
.rb-thumb { position:relative; aspect-ratio:1; border-radius:10px; overflow:hidden; background:var(--line); border:1px solid var(--input); }
.rb-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.rb-thumb__rm { position:absolute; top:0; right:0; width:44px; height:44px; display:flex; align-items:flex-start; justify-content:flex-end; padding:6px; border:0; background:transparent; cursor:pointer; }
.rb-thumb__rm span { width:24px; height:24px; border-radius:999px; background:var(--navy); color:#fff; display:flex; align-items:center; justify-content:center; }

.rb-foot { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:16px; padding-top:24px; border-top:1px solid var(--line); }
.rb-foot__note { flex:1 1 280px; display:flex; gap:8px; align-items:center; font-size:13px; color:var(--muted); }
.rb-btn { height:48px; padding:0 28px; display:inline-flex; align-items:center; justify-content:center; gap:8px; border-radius:12px; font:700 15px Inter, system-ui, sans-serif; cursor:pointer; text-decoration:none; }
.rb-btn--primary { border:0; background:var(--orange); color:var(--navy); }
.rb-btn--primary:hover { background:#F06A16; }
.rb-btn--primary:disabled { opacity:.6; cursor:progress; }
.rb-btn--ghost { border:1px solid var(--input); background:var(--card); color:var(--text); font-weight:600; }
.rb-submit-error { width:100%; margin:0; font-size:14px; color:var(--error); }

.rb-success { display:flex; flex-direction:column; align-items:center; gap:16px; padding:64px 32px; text-align:center; }
.rb-success__icon { width:80px; height:80px; border-radius:999px; background:#FFF3EA; color:var(--orange-text); display:flex; align-items:center; justify-content:center; }
.rb-success h1 { margin:0; font-size:28px; }
.rb-success p { margin:0; max-width:460px; font-size:16px; line-height:1.55; color:var(--text2); }
.rb-success__id { font-size:13px; color:var(--muted); background:var(--line); border-radius:999px; padding:6px 12px; }
.rb-success__actions { display:flex; flex-wrap:wrap; justify-content:center; gap:12px; margin-top:12px; }

.rb-reward { display:flex; gap:14px; align-items:flex-start; margin:0 0 28px; padding:16px 18px; background:#FFF3EA; border:1px solid #F5D3BB; border-radius:14px; text-align:left; }
.rb-reward__icon { flex-shrink:0; width:40px; height:40px; border-radius:999px; background:var(--orange); color:var(--navy); display:flex; align-items:center; justify-content:center; }
.rb-reward__title { display:block; margin-bottom:2px; font-size:15px; font-weight:700; color:var(--text); }
.rb-reward__text { margin:0 !important; font-size:14px !important; line-height:1.5; color:var(--text2) !important; max-width:none !important; }
.rb-success .rb-reward { max-width:460px; margin:4px 0 0; }

@media (max-width: 600px) {
  .rb-top { height:56px; padding:0 8px; justify-content:flex-start; gap:4px; }
  .rb-top__brand, .rb-back__text { display:none; }
  .rb-top__title { display:block; }
  .rb-back { width:44px; justify-content:center; margin-left:0; }
  .rb-wrap { padding:20px 16px 32px; }
  .rb-intro h1 { display:none; }
  .rb-intro p { font-size:14px; margin-bottom:20px; }
  .rb-card { padding:0; border:0; background:transparent; border-radius:0; }
  .rb-form { gap:26px; }
  .rb-label, .rb legend { font-size:14px; }
  .rb-hint { font-size:13px; }
  .rb-chip span { font-size:13px; padding:0 14px; }
  .rb-seg span { height:44px; }
  .rb-thumbs { grid-template-columns:repeat(3, minmax(0,1fr)); gap:10px; }
  .rb-drop { padding:18px 16px; }
  .rb-drop__drag { display:none; }
  .rb input[type="email"] { max-width:none; }
  .rb-foot { border-top:0; padding-top:0; }
  .rb-foot__note { background:var(--card); border:1px solid var(--line); border-radius:10px; padding:12px 14px; color:var(--text2); }
  .rb-form .rb-btn--primary { width:100%; height:52px; font-size:16px; }
  .rb-success { padding:56px 8px; }
  .rb-success__actions { flex-direction:column; width:100%; }
  .rb-success__actions .rb-btn { width:100%; height:52px; }
  .rb-reward { padding:14px; gap:12px; margin-bottom:20px; }
  .rb-reward__icon { width:36px; height:36px; }
}
`;

// ─────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────
const Svg = ({ children, size = 18, sw = 2, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={sw}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
    {...rest}
  >
    {children}
  </svg>
);
const CheckIcon = (p) => (
  <Svg {...p}>
    <path d='M20 6 9 17l-5-5' />
  </Svg>
);
const BackIcon = () => (
  <Svg sw={2.2}>
    <path d='M15 18l-6-6 6-6' />
  </Svg>
);
const UploadIcon = () => (
  <Svg size={22}>
    <path d='M12 16V4' />
    <path d='M7 9l5-5 5 5' />
    <path d='M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3' />
  </Svg>
);
const XIcon = () => (
  <Svg size={12} sw={3}>
    <path d='M18 6 6 18M6 6l12 12' />
  </Svg>
);
const InfoIcon = () => (
  <Svg size={16} style={{ flexShrink: 0 }}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 16v-4M12 8h.01' />
  </Svg>
);
const SendIcon = () => (
  <Svg size={17} sw={2.2}>
    <path d='M22 2 11 13' />
    <path d='M22 2l-7 20-4-9-9-4z' />
  </Svg>
);
const GiftIcon = () => (
  <Svg size={20}>
    <rect x='3' y='8' width='18' height='4' rx='1' />
    <path d='M12 8v13' />
    <path d='M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7' />
    <path d='M7.5 8a2.5 2.5 0 0 1 0-5C10 3 12 8 12 8s2-5 4.5-5a2.5 2.5 0 0 1 0 5' />
  </Svg>
);

// Highlighted reward callout
const RewardNote = ({ title, children }) => (
  <div className='rb-reward'>
    <span className='rb-reward__icon'>
      <GiftIcon />
    </span>
    <div>
      <strong className='rb-reward__title'>{title}</strong>
      <p className='rb-reward__text'>{children}</p>
    </div>
  </div>
);

/**
 * Report a bug — customer web & mobile web.
 *
 * Props:
 *  - source:     "customer_web" | "customer_mobile"   (default "customer_web")
 *  - appVersion: string sent with the report
 *  - onBack:     called by the back button and "Back to SingleCombo" (default: history.back())
 *
 *
 */

const { FINGER_PRINT_USER } = localStorageKeys;
export default function ReportUiBug({ source = 'customer_web', appVersion = 'web-beta', onBack }) {
  const { onReportUiBugCustomer } = useContext(StoreHeaderDataContext) || {};
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]); // { id, file, url }
  const [fileError, setFileError] = useState('');
  const [descError, setDescError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [reportId, setReportId] = useState(null); // set on success

  const descRef = useRef(null);
  const successRef = useRef(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  // Revoke preview URLs on unmount
  useEffect(() => () => filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)), []);

  useEffect(() => {
    if (reportId !== null && successRef.current) successRef.current.focus();
  }, [reportId]);

  const goBack = () => (onBack ? onBack() : window.history.back());

  const update = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'description' && value.trim()) setDescError(false);
  };

  // ───── Attachments ─────
  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;

    const errors = [];
    const next = [...filesRef.current];
    incoming.forEach((file) => {
      if (!file.type || !file.type.startsWith('image/'))
        return errors.push(`${file.name} isn't an image`);
      if (file.size > MAX_SIZE) return errors.push(`${file.name} is over 10 MB`);
      if (next.length >= MAX_FILES) return errors.push(`You can attach up to ${MAX_FILES} images`);
      next.push({ id: `${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file) });
    });
    filesRef.current = next;
    setFiles(next);
    setFileError([...new Set(errors)].join('. '));
  };

  const removeFile = (id) => {
    setFiles((current) =>
      current.filter((f) => {
        if (f.id === id) URL.revokeObjectURL(f.url);
        return f.id !== id;
      })
    );
    setFileError('');
  };

  // Paste screenshots from clipboard (desktop)
  useEffect(() => {
    const onPaste = (e) => {
      if (reportId !== null) return;
      const pasted = e.clipboardData && e.clipboardData.files;
      if (pasted && pasted.length) addFiles(pasted);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [reportId]);

  // ───── Submit ─────
  const DEFAULT_SUBMIT_ERROR = 'Something went wrong sending your report. Please try again.';
  const gettingFingerPrint = () => localStorage.getItem(FINGER_PRINT_USER);

  // Builds the multipart payload the backend expects
  const buildReportPayload = () => {
    const data = new FormData();
    data.append('source', source);
    data.append('type', form.type);
    data.append('where', form.where);
    data.append('frequency', form.frequency);
    data.append('description', form.description.trim());
    data.append('email', form.email.trim());
    data.append('contact', form.contact ? 'yes' : 'no');
    files.forEach((f) => data.append('images', f.file, f.file.name));

    // Auto-collected
    data.append('deviceId', gettingFingerPrint());
    data.append('pageUrl', document.referrer || window.location.href);
    data.append('userAgent', navigator.userAgent);
    data.append('viewport', `${window.innerWidth}x${window.innerHeight}`);
    data.append('language', navigator.language || '');
    data.append('appVersion', appVersion);
    data.append('reportedAt', new Date().toISOString());

    return data;
  };

  // Calls the API controller — expects { success, message, id }
  const SubmitReportBug = async () => {
    setSending(true);
    try {
      const response = await onReportUiBugCustomer(buildReportPayload());

      // TODO: adjust to your controller's return shape (e.g. response.data if it returns the raw axios response)
      const result = response?.data ?? response;

      if (!result || result.success === false) {
        setSubmitError(result?.message || DEFAULT_SUBMIT_ERROR);
        return;
      }

      setReportId(result.id || '');
      window.scrollTo(0, 0);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || DEFAULT_SUBMIT_ERROR);
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!form.description.trim()) {
      setDescError(true);
      if (descRef.current) descRef.current.focus();
      return;
    }

    SubmitReportBug();
  };

  const reset = () => {
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
    setForm(initialForm);
    setFileError('');
    setDescError(false);
    setSubmitError('');
    setReportId(null);
    window.scrollTo(0, 0);
  };

  const sent = reportId !== null;

  return (
    <div className='rb'>
      <style>{css}</style>

      <header className='rb-top'>
        <button type='button' className='rb-back' onClick={goBack} aria-label='Back to Home'>
          <BackIcon />
          <span className='rb-back__text'>Back to Home</span>
        </button>
        <span className='rb-top__title' aria-hidden='true'>
          Report a bug
        </span>
      </header>

      <div className='rb-wrap'>
        {!sent ? (
          <>
            <div className='rb-intro'>
              <h1>Report a bug</h1>
              <p>
                Sorry you hit a snag. Tell us what went wrong — the more detail you share, the
                faster we can fix it.
              </p>
              <RewardNote title='Find a bug, get a treat on us'>
                If your report helps us track down a real issue, we'll add a free item to your next
                order as a thank-you. Leave your email below so we can let you know.
              </RewardNote>
            </div>

            <form className='rb-card rb-form' onSubmit={handleSubmit} noValidate>
              {/* Issue type */}
              <fieldset>
                <legend>What kind of issue?</legend>
                <div className='rb-chips'>
                  {ISSUE_TYPES.map((t) => (
                    <label className='rb-chip' key={t.value}>
                      <input
                        type='radio'
                        name='type'
                        value={t.value}
                        checked={form.type === t.value}
                        onChange={update('type')}
                      />
                      <span>
                        <CheckIcon size={14} sw={2.6} />
                        {t.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Where + frequency */}
              <div className='rb-row2'>
                <div className='rb-field'>
                  <label className='rb-label' htmlFor='rb-where'>
                    Where did it happen?
                  </label>
                  <select id='rb-where' value={form.where} onChange={update('where')}>
                    {LOCATIONS.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>

                <fieldset>
                  <legend>How often does it happen?</legend>
                  <div className='rb-seg'>
                    {FREQUENCIES.map((f) => (
                      <label key={f.value}>
                        <input
                          type='radio'
                          name='frequency'
                          value={f.value}
                          checked={form.frequency === f.value}
                          onChange={update('frequency')}
                        />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Description */}
              <div className='rb-field'>
                <label className='rb-label' htmlFor='rb-desc'>
                  What happened?
                </label>
                <span className='rb-hint' id='rb-desc-hint'>
                  What were you trying to do, and what went wrong?
                </span>
                <textarea
                  id='rb-desc'
                  ref={descRef}
                  rows={6}
                  maxLength={3000}
                  value={form.description}
                  onChange={update('description')}
                  aria-invalid={descError ? 'true' : undefined}
                  aria-describedby={descError ? 'rb-desc-hint rb-desc-error' : 'rb-desc-hint'}
                  placeholder="e.g. I tapped Place order and the screen went blank. My card wasn't charged."
                />
                {descError && (
                  <span className='rb-error' id='rb-desc-error' role='alert'>
                    Please tell us what happened so we can look into it.
                  </span>
                )}
              </div>

              {/* Screenshots */}
              <div className='rb-field'>
                <div className='rb-shots-head'>
                  <span className='rb-label' id='rb-shots-label'>
                    Screenshots <span className='rb-opt'>(optional)</span>
                  </span>
                  <span className='rb-count'>
                    {files.length} of {MAX_FILES}
                  </span>
                </div>

                {files.length < MAX_FILES && (
                  <label
                    className={`rb-drop${dragOver ? ' is-over' : ''}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      addFiles(e.dataTransfer && e.dataTransfer.files);
                    }}
                  >
                    <span className='rb-drop__icon'>
                      <UploadIcon />
                    </span>
                    <span className='rb-drop__main'>
                      <span className='rb-drop__drag'>Drag &amp; drop images here, or </span>
                      <u>browse</u> to add images
                    </span>
                    <span className='rb-drop__sub'>
                      Up to {MAX_FILES} images · PNG, JPG, WEBP or HEIC · 10 MB each
                    </span>
                    <input
                      type='file'
                      className='rb-sr'
                      accept='image/*'
                      multiple
                      aria-labelledby='rb-shots-label'
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}

                {fileError && (
                  <span className='rb-error' role='alert'>
                    {fileError}.
                  </span>
                )}

                {files.length > 0 && (
                  <ul className='rb-thumbs' aria-label='Attached screenshots'>
                    {files.map((f) => (
                      <li className='rb-thumb' key={f.id}>
                        <img src={f.url} alt={f.file.name} />
                        <button
                          type='button'
                          className='rb-thumb__rm'
                          onClick={() => removeFile(f.id)}
                          aria-label={`Remove ${f.file.name}`}
                        >
                          <span>
                            <XIcon />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Contact */}
              <div className='rb-field'>
                <label className='rb-label' htmlFor='rb-email'>
                  Email <span className='rb-opt'>(optional)</span>
                </label>
                <input
                  id='rb-email'
                  type='email'
                  autoComplete='email'
                  placeholder='you@example.com'
                  value={form.email}
                  onChange={update('email')}
                />
                <label className='rb-check'>
                  <input type='checkbox' checked={form.contact} onChange={update('contact')} />
                  It's OK to contact me about this report
                </label>
              </div>

              {/* Footer */}
              <div className='rb-foot'>
                <span className='rb-foot__note'>
                  <InfoIcon />
                  Your device, browser and the page you were on are attached automatically.
                </span>
                <button type='submit' className='rb-btn rb-btn--primary' disabled={sending}>
                  <SendIcon />
                  {sending ? 'Sending…' : 'Send report'}
                </button>
                {submitError && (
                  <p className='rb-submit-error' role='alert'>
                    {submitError}
                  </p>
                )}
              </div>
            </form>
          </>
        ) : (
          <section className='rb-card rb-success' ref={successRef} tabIndex={-1} aria-live='polite'>
            <div className='rb-success__icon' aria-hidden='true'>
              <CheckIcon size={38} sw={2.4} />
            </div>
            <h1>Thanks — we got it!</h1>
            <p>
              Every report helps make SingleCombo better. Our team will review it, and if it turns
              out to be a qualifying bug, your next order comes with a little something extra.
            </p>
            <RewardNote title="Qualifying bug? Treat's on us.">
              {form.email.trim()
                ? "We'll email you when your free item is ready for your next order."
                : 'Next time, add your email so we can tell you when your free item is ready.'}
            </RewardNote>
            {reportId && <span className='rb-success__id'>Report ID: {reportId}</span>}
            <div className='rb-success__actions'>
              <button type='button' className='rb-btn rb-btn--primary' onClick={goBack}>
                Back to SingleCombo
              </button>
              <button type='button' className='rb-btn rb-btn--ghost' onClick={reset}>
                Report another issue
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
